import { useEffect, useState } from "react";
import GridPage from "components/layout/GridPage";
import {
  Box,
  Typography,
  IconButton,
  MenuItem,
  FormControl,
  Button,
} from "@mui/material";
import Select from "components/common/Select";
import Loading from "components/common/Loading";
import SearchAdvisorSelect from "components/common/SearchAdvisorSelect";
import { UserAvatar } from "components/common/CurrentUserAvatar";
import {
  useListAvailableBadges,
  useGetUserBadges,
  useUpdateUserBadge,
  useListUsersByBadge,
} from "services/admin";
import useIsMobile from "components/hooks/useIsMobile";
import { ChevronUp, ChevronDown, Award } from "lucide-react";
import {
  BADGE_CONFIG,
  ACTIVE_BADGES,
  DEFAULT_BADGE_COLOR,
} from "common/badgeConfig";

interface BadgeRowProps {
  badgeName: string;
  count: number;
  onIncrement: () => void;
  onDecrement: () => void;
  disabled: boolean;
  isActive: boolean;
}

const BadgeRow = ({
  badgeName,
  count,
  onIncrement,
  onDecrement,
  disabled,
  isActive,
}: BadgeRowProps) => {
  const config = BADGE_CONFIG[badgeName];
  const BadgeIcon = config?.icon || Award;
  const badgeLabel = config?.label || badgeName;
  const badgeColor =
    config?.color === DEFAULT_BADGE_COLOR ? undefined : config?.color;
  const isSingular = config?.isSingular || false;

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 24px",
        borderBottom: `1px solid ${theme.palette.border}`,
        "&:last-child": {
          borderBottom: "none",
        },
        opacity: isActive ? 1 : 0.5,
        backgroundColor: isActive
          ? theme.palette.background.paper
          : theme.palette.background.default,
      })}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
        <Box
          sx={(theme) => ({
            color: isActive
              ? badgeColor || theme.palette.text.secondary
              : theme.palette.text.disabled,
          })}
        >
          <BadgeIcon size={24} strokeWidth={2} />
        </Box>
        <Typography
          variant="body"
          sx={(theme) => ({
            fontWeight: 500,
            color: isActive
              ? theme.palette.text.primary
              : theme.palette.text.disabled,
            textDecoration: isActive ? "none" : "line-through",
          })}
        >
          {badgeLabel}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
        }}
      >
        <Typography
          variant="body"
          sx={(theme) => ({
            minWidth: "40px",
            textAlign: "center",
            fontWeight: 600,
            color: isActive
              ? theme.palette.text.primary
              : theme.palette.text.disabled,
          })}
        >
          {count}
        </Typography>
        <Box sx={{ display: "flex", gap: "4px" }}>
          <IconButton
            onClick={onIncrement}
            disabled={disabled || !isActive || (isSingular && count >= 1)}
            size="small"
          >
            <ChevronUp size={20} />
          </IconButton>
          <IconButton
            onClick={onDecrement}
            disabled={disabled || count === 0 || !isActive}
            size="small"
          >
            <ChevronDown size={20} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default () => {
  const isMobile = useIsMobile();
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedBadge, setSelectedBadge] = useState<string | null>(null);
  const [badgeToAdd, setBadgeToAdd] = useState<string>("");
  const [localBadgeCounts, setLocalBadgeCounts] = useState<
    Record<string, number>
  >({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const {
    data: badgesData,
    loading: badgesLoading,
    request: requestBadges,
  } = useListAvailableBadges();
  const {
    data: userBadgesData,
    loading: userBadgesLoading,
    request: requestUserBadges,
  } = useGetUserBadges();
  const {
    data: badgeUsersData,
    loading: badgeUsersLoading,
    request: requestBadgeUsers,
  } = useListUsersByBadge();
  const { loading: updating, request: updateBadge } = useUpdateUserBadge(() => {
    if (selectedUser) {
      requestUserBadges({ userRef: selectedUser });
    }
    if (selectedBadge) {
      requestBadgeUsers({ badgeName: selectedBadge });
    }
  });

  useEffect(() => {
    requestBadges();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      setIsInitialLoad(true);
      requestUserBadges({ userRef: selectedUser });
    }
  }, [selectedUser]);

  useEffect(() => {
    if (selectedBadge) {
      requestBadgeUsers({ badgeName: selectedBadge });
    }
  }, [selectedBadge]);

  useEffect(() => {
    if (userBadgesData?.badges) {
      const counts: Record<string, number> = {};
      userBadgesData.badges.forEach((badge) => {
        counts[badge.badgeName] = badge.count;
      });
      setLocalBadgeCounts(counts);
      setIsInitialLoad(false);
    }
  }, [userBadgesData]);

  const handleBadgeUpdate = (
    badgeName: string,
    increment: boolean,
    userRef?: string | null,
  ) => {
    const targetUserRef = userRef || selectedUser;
    if (!targetUserRef) return;

    // Optimistic update
    if (selectedUser) {
      setLocalBadgeCounts((prev) => {
        const currentCount = prev[badgeName] || 0;
        const newCount = increment
          ? currentCount + 1
          : Math.max(0, currentCount - 1);
        return { ...prev, [badgeName]: newCount };
      });
    }

    updateBadge({ userRef: targetUserRef, badgeName, increment });
  };

  const getBadgeCount = (badgeName: string): number => {
    return localBadgeCounts[badgeName] || 0;
  };

  const getUnearnedBadges = (): string[] => {
    if (!badgesData?.badgeNames) return [];
    return badgesData.badgeNames
      .filter((name) => ACTIVE_BADGES.includes(name))
      .filter((name) => getBadgeCount(name) === 0);
  };

  const handleAddBadge = () => {
    if (!badgeToAdd || !selectedUser) return;
    handleBadgeUpdate(badgeToAdd, true);
    setBadgeToAdd("");
  };

  return (
    <GridPage
      sx={{
        padding: isMobile ? "32px 24px" : "48px",
        maxWidth: "1000px",
        margin: isMobile ? "" : "auto",
      }}
    >
      <Typography variant="title" id="tableTitle">
        Badge Management
      </Typography>

      <Box
        sx={{
          marginTop: "32px",
          marginBottom: "32px",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: "16px",
        }}
      >
        <FormControl fullWidth>
          <SearchAdvisorSelect
            selectedAdvisor={selectedUser}
            onChange={(ref) => {
              setSelectedUser(ref);
              setSelectedBadge(null);
            }}
            title="Select User"
          />
        </FormControl>

        <FormControl fullWidth>
          <Select
            variant="detachedOutlined"
            value={selectedBadge || ""}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedBadge(value === "" ? null : value);
              setSelectedUser(null);
            }}
            displayEmpty
            renderValue={(value) => {
              if (!value) return "Select Badge";
              const config = BADGE_CONFIG[value as string];
              return config?.label || value;
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {badgesData?.badgeNames
              ?.filter((name) => ACTIVE_BADGES.includes(name))
              .map((badgeName) => {
                const config = BADGE_CONFIG[badgeName];
                return (
                  <MenuItem key={badgeName} value={badgeName}>
                    {config?.label || badgeName}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      </Box>

      {badgesLoading && <Loading />}

      {selectedBadge && (
        <>
          {badgeUsersLoading && <Loading />}
          {!badgeUsersLoading && badgeUsersData?.users && (
            <>
              <Typography variant="h4" sx={{ marginBottom: "16px" }}>
                Users with {BADGE_CONFIG[selectedBadge]?.label || selectedBadge}{" "}
                badge ({badgeUsersData.users.length})
              </Typography>
              <Box
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.border}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: theme.palette.background.paper,
                })}
              >
                {badgeUsersData.users.map((userBadge) => (
                  <Box
                    key={userBadge.user?.ref}
                    sx={(theme) => ({
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "16px 24px",
                      borderBottom: `1px solid ${theme.palette.border}`,
                      "&:last-child": {
                        borderBottom: "none",
                      },
                    })}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flex: 1,
                      }}
                    >
                      {userBadge.user && (
                        <>
                          <UserAvatar user={userBadge.user} size={40} />
                          <Typography variant="body" sx={{ fontWeight: 500 }}>
                            {userBadge.user.displayName}
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      <Typography
                        variant="body"
                        sx={{
                          minWidth: "40px",
                          textAlign: "center",
                          fontWeight: 600,
                        }}
                      >
                        {userBadge.count}
                      </Typography>
                      <Box sx={{ display: "flex", gap: "4px" }}>
                        <IconButton
                          onClick={() =>
                            handleBadgeUpdate(
                              selectedBadge,
                              true,
                              userBadge.user?.ref,
                            )
                          }
                          disabled={
                            updating ||
                            (BADGE_CONFIG[selectedBadge]?.isSingular &&
                              userBadge.count >= 1)
                          }
                          size="small"
                        >
                          <ChevronUp size={20} />
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            handleBadgeUpdate(
                              selectedBadge,
                              false,
                              userBadge.user?.ref,
                            )
                          }
                          disabled={updating || userBadge.count === 0}
                          size="small"
                        >
                          <ChevronDown size={20} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </>
      )}

      {selectedUser && (
        <>
          {isInitialLoad && userBadgesLoading && <Loading />}
          {(!isInitialLoad || !userBadgesLoading) && badgesData?.badgeNames && (
            <>
              {badgesData.badgeNames
                .filter((name) => ACTIVE_BADGES.includes(name))
                .filter((name) => getBadgeCount(name) > 0).length > 0 && (
                <Box
                  sx={(theme) => ({
                    border: `1px solid ${theme.palette.border}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: theme.palette.background.paper,
                    marginBottom: "16px",
                  })}
                >
                  {badgesData.badgeNames
                    .filter((name) => ACTIVE_BADGES.includes(name))
                    .filter((name) => getBadgeCount(name) > 0)
                    .map((badgeName) => (
                      <BadgeRow
                        key={badgeName}
                        badgeName={badgeName}
                        count={getBadgeCount(badgeName)}
                        onIncrement={() => handleBadgeUpdate(badgeName, true)}
                        onDecrement={() => handleBadgeUpdate(badgeName, false)}
                        disabled={updating}
                        isActive={ACTIVE_BADGES.includes(badgeName)}
                      />
                    ))}
                </Box>
              )}

              {getUnearnedBadges().length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <FormControl sx={{ flex: 1 }}>
                    <Select
                      variant="detachedOutlined"
                      value={badgeToAdd}
                      onChange={(e) => setBadgeToAdd(e.target.value)}
                      displayEmpty
                      renderValue={(value) => {
                        if (!value) return "Select badge to add";
                        const config = BADGE_CONFIG[value as string];
                        return config?.label || value;
                      }}
                    >
                      <MenuItem value="">
                        <em>Select badge to add</em>
                      </MenuItem>
                      {getUnearnedBadges().map((badgeName) => {
                        const config = BADGE_CONFIG[badgeName];
                        return (
                          <MenuItem key={badgeName} value={badgeName}>
                            {config?.label || badgeName}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <Button
                    onClick={handleAddBadge}
                    disabled={!badgeToAdd || updating}
                    variant="contained"
                  >
                    Add badge
                  </Button>
                </Box>
              )}
            </>
          )}
        </>
      )}
    </GridPage>
  );
};
